import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import styles from './TransactionsList.module.scss';
import TransactionBlock from './TransactionBlock';

import ChevronDown from '../../assets/icons/Chevron_Down.svg';
import SearchIcon from '../../assets/icons/Search_Magnifying_Glass.svg';

const DAYS_STEP = 7;
const DAY_MS = 86400000;

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Всё время' },
  { value: 'day', label: 'Сегодня' },
  { value: 'week', label: '7 дней' },
  { value: 'month', label: '30 дней' },
  { value: 'year', label: 'Год' },
];

const DEFAULT_FILTERS = {
  type: 'all',
  category: 'all',
  period: 'all',
  search: '',
};

const SHARE_KEYS = ['type', 'period'];

function readShareFromUrl() {
  const sp = new URLSearchParams(window.location.search);

  const next = {
    type: sp.get('type') || DEFAULT_FILTERS.type,
    period: sp.get('period') || DEFAULT_FILTERS.period,
  };

  if (!['all', 'income', 'expense'].includes(next.type)) next.type = 'all';
  if (!['all', 'day', 'week', 'month', 'year'].includes(next.period))
    next.period = 'all';

  return next;
}

function writeShareToUrl(filters) {
  const sp = new URLSearchParams(window.location.search);

  sp.delete('category');
  sp.delete('search');

  const setOrDelete = (key, val, defVal) => {
    const v = String(val ?? '');
    if (!v || v === defVal) sp.delete(key);
    else sp.set(key, v);
  };

  setOrDelete('type', filters.type, DEFAULT_FILTERS.type);
  setOrDelete('period', filters.period, DEFAULT_FILTERS.period);

  const qs = sp.toString();
  const nextUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}${
    window.location.hash || ''
  }`;
  window.history.replaceState(null, '', nextUrl);
}

function shareEqual(a, b) {
  return SHARE_KEYS.every((k) => String(a?.[k] ?? '') === String(b?.[k] ?? ''));
}

const pad2 = (n) => String(n).padStart(2, '0');

// "YYYY-MM-DD HH:mm:ss"
function parseMysqlDatetime(value) {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;

  const s = String(value).trim();
  if (!s) return null;

  // "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
  const isoLike = s.includes('T') ? s : s.replace(' ', 'T');
  const d = new Date(isoLike);
  return Number.isNaN(d.getTime()) ? null : d;
}

function txTimeMs(t) {
  const when = t?.occurred_at || t?.created_at;
  const d = parseMysqlDatetime(when);
  return d ? d.getTime() : 0;
}

function startOfDayTsByMs(ms) {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return 0;
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function formatDayTitle(dayStartTs) {
  const d = new Date(dayStartTs);
  const now = new Date();
  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  ).getTime();
  const diffDays = Math.round((today - dayStartTs) / DAY_MS);

  if (diffDays === 0) return 'Сегодня';
  if (diffDays === 1) return 'Вчера';
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function uniqSorted(arr) {
  return [...new Set(arr.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, 'ru-RU')
  );
}

function getPeriodFromTime(period) {
  if (!period || period === 'all') return null;

  const now = new Date();
  const from = new Date(now);

  if (period === 'day') {
    from.setHours(0, 0, 0, 0);
    return from.getTime();
  }
  if (period === 'week') {
    from.setDate(from.getDate() - 7);
    return from.getTime();
  }
  if (period === 'month') {
    from.setMonth(from.getMonth() - 1);
    return from.getTime();
  }
  if (period === 'year') {
    from.setFullYear(from.getFullYear() - 1);
    return from.getTime();
  }

  return null;
}

function Dropdown({ value, options, onChange, showKindDot = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) =>
      ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const current = options.find((o) => o.value === value);
  const label = current?.label || 'Выбрать';
  const currentKind = current?.kind || 'none';

  return (
    <div ref={ref} className={styles.dd}>
      <button
        type='button'
        className={styles.dd__btn}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}>
        <span className={styles.dd__btnText}>
          {showKindDot ? (
            <span className={styles.dd__btnRow}>
              <span
                className={[
                  styles.dd__dot,
                  styles[`dd__dot--${currentKind}`],
                ].join(' ')}
                aria-hidden='true'
              />
              <span className={styles.dd__label}>{label}</span>
            </span>
          ) : (
            label
          )}
        </span>

        <img
          src={ChevronDown}
          alt=''
          className={[styles.dd__icon, open ? styles.dd__iconOpen : ''].join(
            ' '
          )}
        />
      </button>

      <div
        className={[
          styles.dd__menu,
          open ? styles.dd__menuOpen : styles.dd__menuClosed,
        ].join(' ')}
        aria-hidden={!open}>
        {options.map((o) => {
          const kind = o.kind || 'none';
          return (
            <button
              key={o.value}
              type='button'
              className={
                o.value === value ? styles.dd__itemActive : styles.dd__item
              }
              tabIndex={open ? 0 : -1}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}>
              {showKindDot ? (
                <span className={styles.dd__row}>
                  <span
                    className={[
                      styles.dd__dot,
                      styles[`dd__dot--${kind}`],
                    ].join(' ')}
                    aria-hidden='true'
                  />
                  <span className={styles.dd__label}>{o.label}</span>
                </span>
              ) : (
                o.label
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function TransactionsList({
  items = [],
  loading,
  error,
  onDelete,
  onFilteredChange,
}) {
  const scrollRef = useRef(null);

  const [daysToShow, setDaysToShow] = useState(DAYS_STEP);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const didInitFromUrlRef = useRef(false);

  const [searchDraft, setSearchDraft] = useState('');
  useEffect(() => setSearchDraft(filters.search), [filters.search]);

  useEffect(() => {
    const id = setTimeout(() => {
      const next = searchDraft.trim();
      if (next.length === 0 || next.length >= 3) {
        setFilters((p) => (p.search === next ? p : { ...p, search: next }));
      }
    }, 250);
    return () => clearTimeout(id);
  }, [searchDraft]);

  const categoriesByType = useMemo(() => {
    const expense = uniqSorted(
      items
        .filter((t) => t.type === 'expense')
        .map((t) => (t.category_name || '—').trim())
    );
    const income = uniqSorted(
      items
        .filter((t) => t.type === 'income')
        .map((t) => (t.category_name || '—').trim())
    );
    const all = uniqSorted([...expense, ...income]);

    return {
      expense,
      income,
      all,
      expenseSet: new Set(expense),
      incomeSet: new Set(income),
    };
  }, [items]);

  const categoryOptions = useMemo(() => {
    const makeKind = (name) => {
      const cat = String(name || '').trim();
      if (!cat || cat === 'all') return 'none';

      const inIncome = categoriesByType.incomeSet.has(cat);
      const inExpense = categoriesByType.expenseSet.has(cat);

      if (inIncome && inExpense) return 'both';
      if (inIncome) return 'income';
      if (inExpense) return 'expense';
      return 'none';
    };

    const list =
      filters.type === 'expense'
        ? categoriesByType.expense
        : filters.type === 'income'
        ? categoriesByType.income
        : categoriesByType.all;

    return [
      { value: 'all', label: 'Все категории', kind: 'none' },
      ...list.map((c) => ({ value: c, label: c, kind: makeKind(c) })),
    ];
  }, [filters.type, categoriesByType]);

  useEffect(() => {
    if (didInitFromUrlRef.current) return;

    const shared = readShareFromUrl();
    didInitFromUrlRef.current = true;

    setFilters((prev) => {
      const next = { ...prev, ...shared };
      return shareEqual(prev, next) ? prev : next;
    });
  }, []);

  useEffect(() => {
    if (!didInitFromUrlRef.current) return;

    const onPop = () => {
      const shared = readShareFromUrl();
      setFilters((prev) => {
        const next = { ...prev, ...shared };
        return shareEqual(prev, next) ? prev : next;
      });
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const applyFilters = (next) => {
    setFilters((prev) => {
      let f = typeof next === 'function' ? next(prev) : next;

      if (f.category && f.category !== 'all') {
        const cat = String(f.category).trim();
        const inIncome = categoriesByType.incomeSet.has(cat);
        const inExpense = categoriesByType.expenseSet.has(cat);

        if (inIncome && !inExpense) f = { ...f, type: 'income' };
        else if (inExpense && !inIncome) f = { ...f, type: 'expense' };
      }

      if (f.category && f.category !== 'all') {
        const cat = String(f.category).trim();
        if (f.type === 'income' && !categoriesByType.incomeSet.has(cat)) {
          f = { ...f, category: 'all' };
        }
        if (f.type === 'expense' && !categoriesByType.expenseSet.has(cat)) {
          f = { ...f, category: 'all' };
        }
      }

      return f;
    });
  };

  const filteredItems = useMemo(() => {
    let list = items;

    list = [...list].sort((a, b) => txTimeMs(b) - txTimeMs(a));

    if (filters.type !== 'all')
      list = list.filter((t) => t.type === filters.type);

    if (filters.category !== 'all') {
      list = list.filter(
        (t) => (t.category_name || '—').trim() === filters.category
      );
    }

    const from = getPeriodFromTime(filters.period);
    if (from) list = list.filter((t) => txTimeMs(t) >= from);

    const q = (filters.search || '').trim().toLowerCase();
    if (q.length >= 3) {
      list = list.filter((t) =>
        String(t.description || '')
          .toLowerCase()
          .includes(q)
      );
    }

    return list;
  }, [items, filters]);

  useEffect(() => {
    onFilteredChange?.(filteredItems);
  }, [filteredItems, onFilteredChange]);

  useEffect(() => {
    setDaysToShow(DAYS_STEP);
  }, [filters, filteredItems.length]);

  const grouped = useMemo(() => {
    const map = new Map();

    for (const t of filteredItems) {
      const dayStart = startOfDayTsByMs(txTimeMs(t));
      if (!map.has(dayStart)) map.set(dayStart, []);
      map.get(dayStart).push(t);
    }

    return Array.from(map.entries())
      .map(([dayStart, list]) => ({
        dayStart,
        items: list,
      }))
      .sort((a, b) => b.dayStart - a.dayStart);
  }, [filteredItems]);

  const visibleDays = grouped.slice(0, daysToShow);
  const hasMore = daysToShow < grouped.length;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (!hasMore) return;

      const nearBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 400;

      if (nearBottom) {
        setDaysToShow((v) => Math.min(grouped.length, v + DAYS_STEP));
      }
    };

    el.addEventListener('scroll', onScroll);
    onScroll();

    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMore, grouped.length]);

  useEffect(() => {
    if (!didInitFromUrlRef.current) return;
    writeShareToUrl(filters);
  }, [filters.type, filters.period]);

  return (
    <div className={styles.transactions}>
      <div className={styles.transactions__inner}>
        <div className={styles.transactions__filter}>
          <div className={styles.filter__type}>
            <div
              className={[
                styles['filter__type--item'],
                filters.type === 'all' ? styles.active : '',
              ].join(' ')}
              onClick={() =>
                applyFilters((p) => ({ ...p, type: 'all', category: 'all' }))
              }>
              Все
            </div>

            <div
              className={[
                styles['filter__type--item'],
                filters.type === 'income' ? styles.active : '',
              ].join(' ')}
              onClick={() =>
                applyFilters((p) => ({ ...p, type: 'income', category: 'all' }))
              }>
              Доход
            </div>

            <div
              className={[
                styles['filter__type--item'],
                filters.type === 'expense' ? styles.active : '',
              ].join(' ')}
              onClick={() =>
                applyFilters((p) => ({
                  ...p,
                  type: 'expense',
                  category: 'all',
                }))
              }>
              Расход
            </div>

            <div
              className={styles.filter__typeIndicator}
              style={{
                transform:
                  filters.type === 'all'
                    ? 'translateX(0%)'
                    : filters.type === 'income'
                    ? 'translateX(100%)'
                    : 'translateX(200%)',
              }}
            />
          </div>

          <Dropdown
            value={filters.category}
            options={categoryOptions}
            onChange={(v) => applyFilters((p) => ({ ...p, category: v }))}
            showKindDot
          />

          <Dropdown
            value={filters.period}
            options={PERIOD_OPTIONS}
            onChange={(v) => applyFilters((p) => ({ ...p, period: v }))}
          />

          <div className={styles.filter__searchWrap}>
            <img
              src={SearchIcon}
              alt=''
              className={styles.filter__searchIcon}
            />

            <input
              className={styles.filter__search}
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder='Поиск...'
              type='text'
            />
          </div>
        </div>

        <div className={styles.transactions__scroll} ref={scrollRef}>
          {loading ? (
            <div className={styles.transactions__state}>Загрузка...</div>
          ) : error ? (
            <div className={styles.transactions__state}>{error}</div>
          ) : filteredItems.length === 0 ? (
            <div className={styles.transactions__state}>
              {filters.search.trim().length > 0 &&
              filters.search.trim().length < 3
                ? 'Введите минимум 3 символа'
                : 'Ничего не найдено'}
            </div>
          ) : (
            <div className={styles.transactions__list}>
              {visibleDays.map((day) => (
                <React.Fragment key={day.dayStart}>
                  <div className={styles.transactions__date}>
                    <h2 className={styles['transactions__date--day']}>
                      {formatDayTitle(day.dayStart)}
                    </h2>
                    <div className={styles.transactions__line} />
                  </div>

                  <div className={styles.transactions__blocks}>
                    <AnimatePresence initial={false}>
                      {day.items.map((t) => (
                        <TransactionBlock
                          key={t.id}
                          t={t}
                          onDelete={onDelete}
                          onHashtagClick={(tag) => setSearchDraft(tag)}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                </React.Fragment>
              ))}

              {hasMore ? (
                <div className={styles.transactions__state}>Загрузка...</div>
              ) : null}
            </div>
          )}
        </div>

        <div className={styles.transactions__gradient} />
      </div>
    </div>
  );
}
